<script lang="ts">
import { onMount } from 'svelte'

import Switch from '@/components/core/Switch.svelte'
import Field from '@/components/form/Field.svelte'
import SelectField from '@/components/form/SelectField.svelte'
import ValidationMessage from '@/components/form/ValidationMessage.svelte'
import { testIds } from '@/constants'
import {
	type AuthFormName,
	type AuthFormValues,
	createAuthFormControls,
	formFieldOverriden,
	loadAuthFormFromStorage,
	removeAuthFormFromStorage,
	saveAuthFormFieldToStorage,
	submitAuthForm,
} from '@/modules/auth'
import { errorMessage, successMessage } from '@/modules/flashMessages'
import {
	createForm,
	FormControlType,
	getFormControlValidators,
	getFormControlValues,
	INPUT_MAXLENGTH,
} from '@/modules/form'
import { t } from '@/modules/translations'
import { visitor } from '@/modules/visitor'
import { printError } from '@/utils/console'

import AuthFormPrivacyNotice from './AuthFormPrivacyNotice.svelte'

type Props = {
	id: string
	isSubmitting?: boolean
}

let { id, isSubmitting = $bindable(false) }: Props = $props()

const controls = createAuthFormControls($visitor, $formFieldOverriden)

const {
	form,
	errors,
	isSubmitting: isFormSubmitting,
	setFieldValue,
	setValues,
	handleChange,
	handleSubmit,
} = createForm({
	initialValues: getFormControlValues<AuthFormValues, typeof controls>(controls),
	validators: getFormControlValidators(controls),
	onChange: (field: AuthFormName, value: string) => saveAuthFormFieldToStorage(field, value),
	onSubmit: async (values) => {
		try {
			await submitAuthForm(values)
			removeAuthFormFromStorage()
			successMessage($t('authForm.submit.success'))
		} catch (e) {
			printError(String(e))
			errorMessage($t('authForm.submit.error'))
		}
	},
})

onMount(() => {
	const storedAuthFormValues = loadAuthFormFromStorage()
	if (storedAuthFormValues) setValues(storedAuthFormValues, { shouldValidate: true })
})

const handleSwitchChange = (name: string) => (event: Event) => {
	if (event.target instanceof HTMLInputElement) {
		setFieldValue(name, event.target.checked ? true : false)
	}
}

$effect(() => {
	isSubmitting = $isFormSubmitting
})
</script>

<form {id} onsubmit={handleSubmit} class="mb-0">
	{#each controls as control (control.name)}
		{#if control.type === FormControlType.Text}
			<Field
				bind:value={$form[control.name]}
				name={control.name}
				type={control.subType}
				label={$t(control.label)}
				placeholder={control.placeholder ? $t(control.placeholder) : ''}
				maxLength={control.maxLength ?? INPUT_MAXLENGTH}
				errors={$errors}
				onInput={handleChange}
				onBlur={handleChange}
				onChange={handleChange}
			/>
		{:else if control.type === FormControlType.Select}
			<SelectField
				bind:value={$form[control.name]}
				name={control.name}
				label={$t(control.label)}
				placeholder={control.placeholder ? $t(control.placeholder) : undefined}
				options={control.options}
				errors={$errors}
				onChange={handleChange}
				onBlur={handleChange}
			/>
		{:else if control.type === FormControlType.Checkbox}
			{#snippet controlCmp()}
				{#if !control.isHidden}
					<Switch
						name={control.name}
						bind:value={$form[control.name]}
						onChange={handleSwitchChange(control.name)}
						testId={testIds.authFormPrivacyNoticeSwitch}
					/>
				{/if}
			{/snippet}
			{#snippet feedback()}
				<ValidationMessage name={control.name} errors={$errors} />
			{/snippet}
			<AuthFormPrivacyNotice label={$t(control.label)} isControlHidden={!!control.isHidden} {controlCmp} {feedback} />
		{/if}
	{/each}
</form>
